import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellIcon } from './icons.jsx'
import { EVENT_TYPES, listEvents, relativeTime } from '../lib/events.js'
import { loadActorNames, resolveActor } from '../lib/actorNames.js'
import { scopedKey } from '../lib/userScope.js'

// Bell dropdown fed by the real events log. "Unread" = newer than the last time
// the user opened the dropdown. The events feed is shared by the workspace but
// "have I read it?" is not, so the watermark is per account — otherwise one
// teammate opening the bell zeroes everyone else's badge on the same browser.
const SEEN_BASE = 'revailosales.notifications.seen'
const seenKey = () => scopedKey(SEEN_BASE)

function getSeen() {
  try {
    return localStorage.getItem(seenKey()) || ''
  } catch {
    return ''
  }
}

export default function NotificationsMenu() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  // A conditionally-rendered panel unmounts instantly, so closing was a hard
  // cut against a 0.16s open. `closing` keeps it mounted for the reverse
  // animation, then drops it.
  const [closing, setClosing] = useState(false)
  const closeTimer = useRef(null)
  const [events, setEvents] = useState([])
  const [actorNames, setActorNames] = useState(null)
  useEffect(() => {
    let cancelled = false
    loadActorNames().then((m) => {
      if (!cancelled) setActorNames(m)
    })
    return () => {
      cancelled = true
    }
  }, [])
  const [seenBefore, setSeenBefore] = useState(getSeen)
  const menuRef = useRef(null)

  // Refresh the feed when the dropdown opens (and once on mount for the dot).
  useEffect(() => {
    // Fetch more than the dropdown shows: the badge counts unread, and a
    // limit of 10 would quietly cap the number at 10 no matter the real backlog.
    const load = () => listEvents({ limit: 50 }).then(({ events }) => setEvents(events))
    load()
    // Newly logged events (e.g. an enrichment finishing) land without a remount.
    window.addEventListener('revailosales:event', load)
    return () => window.removeEventListener('revailosales:event', load)
  }, [open])

  useEffect(() => {
    if (!open) return
    function onPointer(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) close()
    }
    function onKey(e) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // A dot only says "something happened"; the count says whether it's worth
  // opening. Capped at 9+ so the badge can't outgrow the icon.
  const unread = events.filter((e) => e.createdAt > seenBefore).length
  const hasUnread = unread > 0

  function markAllRead() {
    const latest = events[0]?.createdAt || new Date().toISOString()
    try {
      localStorage.setItem(seenKey(), latest)
    } catch {
      /* ignore */
    }
    setSeenBefore(latest)
  }

  const CLOSE_MS = 140

  function close() {
    if (!open || closing) return
    setClosing(true)
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => {
      setOpen(false)
      setClosing(false)
    }, CLOSE_MS)
  }

  function toggle() {
    if (open) close()
    else setOpen(true)
  }

  // A pending close must not fire after unmount.
  useEffect(() => () => clearTimeout(closeTimer.current), [])

  // Clicking a notification goes where the thing that happened can be acted on.
  function openEvent(e) {
    const to = EVENT_TYPES[e.type]?.to
    markAllRead()
    close()
    if (to) navigate(to)
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        className="relative grid h-[34px] w-[34px] place-items-center rounded-[9px] border-none bg-transparent text-dim transition-colors hover:bg-panel hover:text-ink [&_svg]:h-[18px] [&_svg]:w-[18px]"
        type="button"
        aria-label="Notifications"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <BellIcon />
        {hasUnread && (
          <span
            className="absolute -right-[2px] -top-[2px] grid h-[17px] min-w-[17px] place-items-center rounded-full border-[1.5px] border-surface bg-primary px-[3px] text-[10.5px] font-bold leading-none text-white tabular-nums"
            aria-label={`${unread} unread notification${unread === 1 ? '' : 's'}`}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute right-0 top-[calc(100%+10px)] z-30 w-[340px] origin-top-right overflow-hidden rounded-[12px] border border-line bg-surface font-sora shadow-[0_12px_32px_rgba(2,6,23,0.16)] ${
            closing ? 'animate-[menuHide_0.14s_ease-in_forwards]' : 'animate-[menuReveal_0.16s_ease]'
          }`}
          role="menu"
        >
          <div className="flex items-center justify-between border-b border-line px-[14px] py-3 font-sora text-[13px] font-semibold text-ink">
            Notifications
            {hasUnread && (
              <button
                className="border-none bg-transparent p-0 font-sora text-[12px] text-primary-dark"
                onClick={markAllRead}
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[340px] overflow-y-auto">
            {events.length === 0 ? (
              <div className="px-[14px] py-[22px] text-center font-sora text-[12.5px] text-dim">
                Nothing yet — workspace activity shows up here.
              </div>
            ) : (
              events.slice(0, 10).map((e) => {
                const unread = e.createdAt > seenBefore
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => openEvent(e)}
                    title={EVENT_TYPES[e.type]?.to ? 'Open where this happened' : undefined}
                    className={`flex w-full cursor-pointer items-start gap-x-[10px] border-0 border-b border-line px-[14px] py-[11px] text-left font-sora text-[12.5px] transition-colors last:border-b-0 ${
                      unread ? 'bg-primary-tint hover:bg-primary-tint/70' : 'bg-transparent hover:bg-panel'
                    }`}
                  >
                    <span
                      className={`mt-[5px] h-[8px] w-[8px] shrink-0 rounded-full ${unread ? 'bg-primary' : 'bg-line'}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-sora font-semibold text-ink">
                        {EVENT_TYPES[e.type]?.label || e.type}
                        {/* Roster name first — stored labels can be email
                            prefixes or demo-era role words. */}
                        {(() => {
                          const who = resolveActor(actorNames, e.actor, e.actorName)
                          return who ? ` — ${who}` : ''
                        })()}
                      </div>
                      {e.detail && (
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap font-sora text-dim">
                          {e.detail}
                        </div>
                      )}
                    </div>
                    <span className="whitespace-nowrap font-sora text-[11.5px] text-faint">
                      {relativeTime(e.createdAt)}
                    </span>
                  </button>
                )
              })
            )}
          </div>
          <div className="border-t border-line px-[14px] py-[9px] text-center">
            <button
              className="border-none bg-transparent p-0 font-sora text-[12px] text-primary-dark"
              onClick={() => {
                markAllRead()
                close()
                navigate('/logs/events')
              }}
            >
              View all events →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
